import { useState, useCallback, useEffect, useRef, useMemo } from "react"

import { isRTableContent, toggleSelection } from "./elementData"
import {
  useElementBase,
  ElementAdapter,
  ElementBaseContext,
} from "./useElementBase"
import { useChartConfig } from "./useElementConfigFactory"
import { useOperatorManagement } from "../useOperatorManagement"

import {
  DEFAULT_CHART_WIDTH,
  DEFAULT_CHART_HEIGHT,
} from "constants/dashboardElementSizes"

import { convertDataToTable } from "utils/convertDataToTable"
import { ScriptGenerationService } from "services/ScriptGenerationService"
import { generateChartImage } from "services/ChartImageService"

import {
  STATISTIC_TYPE,
  CHART_DISPLAY_TYPE,
  ELEMENT_TYPE,
  SlideElement,
  DataElementConfig,
  ChartElementSettings,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { EngineOutputItem } from "types/engineOutput.types"
import { ElementRef } from "../../../../features/Reporting/components/ReportingDisplay/ReportingDisplay.component"

/**
 * Element update payload for chart elements
 */
interface ChartElementUpdate {
  width?: number
  height?: number
}

/**
 * Props for useChartElement hook
 */
interface UseChartElementProps {
  /** Global filter string passed to data elements */
  filter?: string
  /** Element object to render */
  element: SlideElement
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Callback when element properties change */
  onUpdate: (elementId: string, updates: ChartElementUpdate) => void
  /** Configuration for chart element */
  config?: DataElementConfig | null
  /** Callback for config updates */
  onConfigUpdate: (config: DataElementConfig | null) => void
  /** Whether in dashboard mode */
  isDashboard?: boolean
  /** Callback when element deletion is requested */
  onDeleteRequest?: () => void
  /** Sequential execution request handler */
  onElementExecutionRequest?: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void
  /** Element registration handler */
  onRegisterElementRef?: (elementId: string, ref: ElementRef | null) => void
}

type ChartContext = ElementBaseContext<EngineOutputItem>

const getChartVariables = (
  ctx: ChartContext
): { selectedVariable: string | null; breakdownVariable: string | null } => ({
  selectedVariable: ctx.getValue<string | null>("selectedVariable") ?? null,
  breakdownVariable: ctx.getValue<string | null>("breakdownVariable") ?? null,
})

/**
 * Chart adapter for useElementBase.
 *
 * All callbacks receive the latest-render context, so the adapter can live
 * at module level. The executor keeps Chart's pinned quirks: variables are
 * validated BEFORE markAttemptedLoad (silent return), and dashboard
 * defaults are only seeded for multi-key (non-simple) data.
 */
const chartAdapter: ElementAdapter<EngineOutputItem> = {
  elementType: ELEMENT_TYPE.CHART,
  isTimeSeries: false,
  useConfigHook: useChartConfig,
  defaultSize: { width: DEFAULT_CHART_WIDTH, height: DEFAULT_CHART_HEIGHT },
  reactsToFilter: true,
  internalAutoRunEnabled: true,
  internalAutoRunConditions: (ctx): boolean => {
    const { selectedVariable, breakdownVariable } = getChartVariables(ctx)
    const hasVariables = selectedVariable && breakdownVariable
    return !!(
      hasVariables &&
      !ctx.showSelects &&
      ctx.queryInstance &&
      !ctx.configHook.isInitializing
    )
  },
  convertsRTableData: true,
  configShowsData: (config): boolean => {
    const chartConfig = config as ChartElementSettings
    return !!(chartConfig.selectedVariable && chartConfig.breakdownVariable)
  },
  resetDataForConfigLoad: (ctx): void => {
    ctx.setData(null)
  },
  hasRequiredSelections: (ctx): boolean => {
    const { selectedVariable, breakdownVariable } = getChartVariables(ctx)
    return !!(selectedVariable && breakdownVariable)
  },
  autoRunSignature: (ctx): string => {
    const { selectedVariable, breakdownVariable } = getChartVariables(ctx)
    return `${selectedVariable}|${breakdownVariable}`
  },
  hasDataAndShouldRefresh: (ctx): boolean => {
    const { selectedVariable, breakdownVariable } = getChartVariables(ctx)
    return !!(selectedVariable && breakdownVariable && ctx.data)
  },
  /**
   * Core Script Execution Function
   *
   * Orchestrates the complete data fetching pipeline:
   * 1. Validates required variables are selected
   * 2. Generates script using ScriptGenerationService
   * 3. Executes script via reporting data loading hook
   * 4. Updates component state (selection -> display mode)
   * 5. Handles dashboard defaults for multi-series charts
   */
  execute: async (ctx): Promise<void> => {
    const { selectedVariable, breakdownVariable } = getChartVariables(ctx)
    const selectedStatisticType = ctx.getValue<STATISTIC_TYPE>(
      "selectedStatisticType",
      STATISTIC_TYPE.N
    )
    const tableOperationType = ctx.getValue<TABLE_OPERATION_TYPE>(
      "tableOperationType",
      TABLE_OPERATION_TYPE.XTAB
    )
    const selectedDashboardKeys = ctx.getValue<string[]>(
      "dashboardSelectedKeys",
      []
    )
    const selectedDashboardCategories = ctx.getValue<string[]>(
      "dashboardSelectedCategories",
      []
    )

    if (!selectedVariable || !breakdownVariable || ctx.isLoading) return

    ctx.clearError()
    ctx.markAttemptedLoad()

    try {
      // Generate script using the centralized service
      const script = ScriptGenerationService.generateChartScript(
        ctx.element.id,
        selectedVariable,
        breakdownVariable,
        selectedStatisticType,
        tableOperationType,
        ctx.filter
      )

      const result = await ctx.executeScript(script, ctx.element.id)
      if (result.success) {
        ctx.setData(result.data as EngineOutputItem)
        ctx.updateConfigState({ showSelects: false })

        // Dashboard defaults handling
        if (ctx.isDashboard && result.data && "content" in result.data) {
          try {
            if (!isRTableContent(result.data.content)) {
              console.warn("Result data content is not RTableContent format")
              return
            }

            const converted = convertDataToTable(result.data.content)
            const isSimpleDataResult = converted.barChartData.keys.length === 1

            if (!isSimpleDataResult) {
              const defaultKeys = converted.barChartData.keys.filter(
                (k) => k !== "Totals"
              )
              const defaultCategories = converted.barChartData.data
                .filter((i) => i.xValue !== "Totals")
                .map((i) => String(i.xValue))

              ctx.configHook.updateConfig({
                dashboardSelectedKeys: selectedDashboardKeys.length
                  ? selectedDashboardKeys
                  : defaultKeys,
                dashboardSelectedCategories: selectedDashboardCategories.length
                  ? selectedDashboardCategories
                  : defaultCategories,
              })
            }
          } catch (err) {
            console.error("Error processing dashboard defaults:", err)
          }
        }
      }
    } catch (e) {
      console.error(
        `Script execution error for element ${ctx.element.id}:`,
        e
      )
    }
  },
  resetState: (ctx): void => {
    ctx.setData(null)
    ctx.updateConfigState({ showSelects: true })
    ctx.clearError()
    ctx.updateElementState({
      hasAttemptedLoad: false,
      isQueuedForExecution: false,
    })
  },
}

/**
 * Custom Hook: useChartElement
 *
 * Chart element business logic, composed from useElementBase (shared
 * lifecycle: data loading, config wiring, element state, auto-run,
 * filter-change and registration effects, refresh/edit/delete family)
 * plus the chart adapter (script generation, dashboard-defaults seeding)
 * and chart-only extras (isSimpleData, chart-type/totals handlers, the
 * image-generation effect that also persists the config — risk item 9
 * behavior intentionally unchanged).
 *
 * @param props - Chart element configuration and callbacks
 * @returns Object containing all state, handlers, and configuration needed by component
 */
export const useChartElement = (props: UseChartElementProps) => {
  const { element, onUpdate } = props

  const base = useElementBase<EngineOutputItem>(props, chartAdapter)

  const {
    isLoading: isRunningScript,
    error,
    configHook,
    data: chartData,
    showSelects,
    hasAttemptedLoad,
    isQueuedForExecution,
    convertedData: convertedChartData,
    selectOptions,
    requestSequentialExecution,
    hasDataAndShouldRefresh,
    handleRefresh,
    handleEdit,
    handleDelete,
  } = base

  // Get values from config
  const selectedVariable = configHook.getConfigValue<string | null>(
    "selectedVariable"
  )
  const breakdownVariable = configHook.getConfigValue<string | null>(
    "breakdownVariable"
  )
  const selectedStatisticType = configHook.getConfigValue<STATISTIC_TYPE>(
    "selectedStatisticType",
    STATISTIC_TYPE.N
  )
  const chartType = configHook.getConfigValue<CHART_DISPLAY_TYPE>(
    "chartType",
    CHART_DISPLAY_TYPE.BAR
  )
  const showTotals = configHook.getConfigValue<boolean>("showTotals", false)
  const selectedDashboardKeys = configHook.getConfigValue<string[]>(
    "dashboardSelectedKeys",
    []
  )
  const selectedDashboardCategories = configHook.getConfigValue<string[]>(
    "dashboardSelectedCategories",
    []
  )
  const tableOperationType = configHook.getConfigValue<TABLE_OPERATION_TYPE>(
    "tableOperationType",
    TABLE_OPERATION_TYPE.XTAB
  )

  // Use operator management hook for centralized operator handling
  const operatorManagement = useOperatorManagement({
    initialOperationType: tableOperationType,
    initialStatisticType: selectedStatisticType,
    singleStatisticMode: true, // Charts use single statistic selection
    onOperationTypeChange: (newOperationType) => {
      configHook.setConfigValue("tableOperationType", newOperationType)
    },
    onStatisticTypeChange: (newStatisticType) => {
      configHook.setConfigValue("selectedStatisticType", newStatisticType)
    },
  })

  // Check if this is simple data
  const isSimpleData = useMemo((): boolean => {
    if (!convertedChartData) return false
    return convertedChartData.barChartData.keys.length === 1
  }, [convertedChartData])

  // Chart-specific event handlers
  const handleToggleTotals = useCallback((): void => {
    configHook.setConfigValue("showTotals", !showTotals)
  }, [showTotals, configHook])

  const handleChartTypeChange = useCallback(
    (newType: CHART_DISPLAY_TYPE): void => {
      configHook.setConfigValue("chartType", newType)

      if (newType === CHART_DISPLAY_TYPE.HORIZONTAL_BAR) {
        const currentWidth = element.width || DEFAULT_CHART_WIDTH
        const currentHeight = element.height || DEFAULT_CHART_HEIGHT
        if (currentWidth < currentHeight) {
          onUpdate(element.id, {
            width: Math.max(currentWidth, Math.min(800, currentHeight)),
            height: Math.max(300, Math.min(currentWidth, currentHeight)),
          })
        }
      }
    },
    [element.id, element.width, element.height, onUpdate, configHook]
  )

  const handleVariableToggle = useCallback(
    (key: string): void => {
      configHook.setConfigValue(
        "dashboardSelectedKeys",
        toggleSelection(selectedDashboardKeys, key)
      )
    },
    [selectedDashboardKeys, configHook]
  )

  const handleCategoryToggle = useCallback(
    (cat: string): void => {
      configHook.setConfigValue(
        "dashboardSelectedCategories",
        toggleSelection(selectedDashboardCategories, cat)
      )
    },
    [selectedDashboardCategories, configHook]
  )

  // Variable selection handlers
  const handleVariableSelect = useCallback(
    (value: string, type: "primary" | "breakdown"): void => {
      const key = type === "primary" ? "selectedVariable" : "breakdownVariable"
      configHook.setConfigValue(key, value)
    },
    [configHook]
  )

  // Image generation state and refs
  const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false)
  const lastGeneratedConfigRef = useRef<string | null>(null)

  // Memoize sorted keys and categories for dependency array.
  // CURRENT BEHAVIOR (pinned): .sort() mutates the config arrays IN PLACE,
  // so consumers of the config values see them already alphabetized.
  const sortedDashboardKeys = useMemo(
    (): string => selectedDashboardKeys.sort().join(","),
    [selectedDashboardKeys]
  )

  const sortedDashboardCategories = useMemo(
    (): string => selectedDashboardCategories.sort().join(","),
    [selectedDashboardCategories]
  )

  // Image generation effect
  /**
   * Intelligent Image Generation System
   *
   * Automatically generates chart images for export functionality:
   * configuration change detection (config-string dedup), 700ms debounce,
   * generation state management, PowerPoint export data URIs.
   *
   * NOTE (risk item 9, intentionally unchanged): config persistence is a
   * side effect of this effect (configHook.saveConfig). On the auto-run
   * path the skipNextConfigUpdate flag set before execution is consumed
   * here, so the config is NOT persisted at all.
   */
  useEffect(() => {
    if (isGeneratingImage || !chartData || showSelects) {
      return
    }

    const currentConfigString = JSON.stringify({
      selectedVariable,
      breakdownVariable,
      selectedStatisticType,
      chartType,
      showTotals,
      tableOperationType,
      keys: sortedDashboardKeys,
      categories: sortedDashboardCategories,
      // Include dimensions to trigger regeneration on resize
      width: element.width,
      height: element.height,
    })

    if (currentConfigString === lastGeneratedConfigRef.current) {
      return
    }

    const timerId = setTimeout(async () => {
      if (isGeneratingImage) return

      try {
        setIsGeneratingImage(true)
        configHook.isGeneratingImageRef.current = true

        if (!isRTableContent(chartData.content)) {
          console.warn(
            "Chart data content is not RTableContent format for image generation"
          )
          return
        }

        const chartRenderData = { content: chartData.content }

        const dataUri = await generateChartImage(
          ELEMENT_TYPE.CHART,
          chartRenderData,
          {
            selectedVariable,
            breakdownVariable,
            selectedStatisticType,
            chartType,
            showTotals,
            dashboardSelectedKeys: selectedDashboardKeys,
            dashboardSelectedCategories: selectedDashboardCategories,
            width: element.width || DEFAULT_CHART_WIDTH,
            height: element.height || DEFAULT_CHART_HEIGHT,
          }
        )

        lastGeneratedConfigRef.current = currentConfigString

        configHook.saveConfig({
          ...(configHook.config as ChartElementSettings),
          chartDataUri: dataUri,
        })
      } catch (error) {
        console.error("Error generating chart image:", error)
        configHook.saveConfig({
          ...(configHook.config as ChartElementSettings),
          chartDataUri: null,
        })
      } finally {
        setIsGeneratingImage(false)
        setTimeout(() => {
          configHook.isGeneratingImageRef.current = false
        }, 100)
      }
    }, 700)

    return () => clearTimeout(timerId)
  }, [
    chartData,
    showSelects,
    selectedVariable,
    breakdownVariable,
    selectedStatisticType,
    chartType,
    showTotals,
    tableOperationType,
    isGeneratingImage,
    sortedDashboardKeys,
    sortedDashboardCategories,
    element.width,
    element.height,
    configHook,
  ])

  // Return all the state and handlers needed by the component
  return {
    // State
    chartData,
    showSelects,
    isRunningScript,
    isQueuedForExecution,
    error,
    hasAttemptedLoad,
    convertedChartData,
    isSimpleData,
    selectOptions,

    // Config values
    selectedVariable,
    breakdownVariable,
    selectedStatisticType,
    chartType,
    showTotals,
    selectedDashboardKeys,
    selectedDashboardCategories,
    tableOperationType,

    // Hooks
    configHook,
    operatorManagement,

    // Handlers
    handleRefresh,
    handleEdit,
    handleDelete,
    handleToggleTotals,
    handleChartTypeChange,
    handleVariableToggle,
    handleCategoryToggle,
    handleVariableSelect,
    requestSequentialExecution,

    // Imperative handle values
    hasDataAndShouldRefresh,
  }
}
